/**
 * ViewportStatusControls — AutoCAD-style VP scale + lock padlock in the
 * bottom status bar (AutoCAD parity: bottom-right when a paper-space
 * VIEWPORT is selected OR is the current/active viewport in MSPACE_VIA_VP).
 *
 * Shown when (either condition):
 *   A) Exactly one Viewport entity selected in a layout (PSPACE)
 *   B) mode === MSPACE_VIA_VP and spaceContextStore.activeViewportId is set
 *
 * Mutations use the same dispatch actions as ViewportInspector:
 *   geometry/viewport-set-scale + geometry/viewport-set-lock
 */

import * as React from "react";
import type { ViewportEntity } from "@dxf-ai-editor/drawing-model";
import { readViewportOverrides } from "@dxf-ai-editor/drawing-model";
import { useEditorStore } from "../../state/editorStore";
import { useSpaceContextStore } from "../../state/spaceContextStore";
import { useT } from "../../i18n";

// ---------------------------------------------------------------------------
// Scale presets — same standard set as ViewportInspector.
// ---------------------------------------------------------------------------

interface ScalePreset {
  label: string;
  num: number;
  denom: number;
}

const SCALE_PRESETS: ScalePreset[] = [
  { label: "1:1",   num: 1, denom: 1 },
  { label: "1:2",   num: 1, denom: 2 },
  { label: "1:5",   num: 1, denom: 5 },
  { label: "1:10",  num: 1, denom: 10 },
  { label: "1:20",  num: 1, denom: 20 },
  { label: "1:50",  num: 1, denom: 50 },
  { label: "1:100", num: 1, denom: 100 },
  { label: "1:200", num: 1, denom: 200 },
  { label: "1:500", num: 1, denom: 500 },
];

const CUSTOM_SCALE_VALUE = "custom";

function scaleToPresetLabel(num: number, denom: number): string {
  const match = SCALE_PRESETS.find((p) => p.num === num && p.denom === denom);
  return match ? match.label : CUSTOM_SCALE_VALUE;
}

// ---------------------------------------------------------------------------
// SVG icons
// ---------------------------------------------------------------------------

function LockClosedIcon(): React.ReactElement {
  return (
    <svg
      width="11"
      height="13"
      viewBox="0 0 11 13"
      fill="none"
      aria-hidden="true"
    >
      <rect x="1" y="5.5" width="9" height="7" rx="1.5" fill="currentColor" opacity="0.85" />
      <path
        d="M3 5.5V3.5a2.5 2.5 0 0 1 5 0v2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

function LockOpenIcon(): React.ReactElement {
  return (
    <svg
      width="11"
      height="13"
      viewBox="0 0 11 13"
      fill="none"
      aria-hidden="true"
    >
      <rect x="1" y="5.5" width="9" height="7" rx="1.5" fill="currentColor" opacity="0.55" />
      <path
        d="M3 5.5V3.5a2.5 2.5 0 0 1 5 0"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// findViewportById — searches all spaces in a geometry document.
// Declared before VpScaleLock so the hook inside VpScaleLock can call it.
// ---------------------------------------------------------------------------

function findViewportById(
  geometryDocument: { spaces: Array<{ entities: unknown[] }> },
  vpId: string,
): ViewportEntity | null {
  for (const space of geometryDocument.spaces) {
    const found = space.entities.find(
      (e) => (e as { id: string; type: string }).id === vpId &&
              (e as { id: string; type: string }).type === "Viewport",
    );
    if (found) return found as ViewportEntity;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Inner scale+lock widget — subscribes directly to the live VP entity from
// the store by ID so the lock/scale state is always current without relying
// on the parent to pass an up-to-date prop.
// ---------------------------------------------------------------------------

interface VpScaleLockProps {
  viewportId: string;
}

function VpScaleLock({ viewportId }: VpScaleLockProps): React.ReactElement | null {
  const t = useT();
  const dispatch = useEditorStore((s) => s.dispatch);
  const viewport = useEditorStore((s) => findViewportById(s.geometryDocument, viewportId));

  if (!viewport) return null;

  const overrides = readViewportOverrides(viewport);
  const { scale, locked } = overrides;

  // Same OR semantics as ViewportInspector (iter-5 P2 fix #3).
  const effectiveLocked = (viewport.displayLocked ?? false) || locked;
  const presetLabel = scaleToPresetLabel(scale.num, scale.denom);
  const selectValue = presetLabel === CUSTOM_SCALE_VALUE ? CUSTOM_SCALE_VALUE : presetLabel;

  function handleScaleChange(e: React.ChangeEvent<HTMLSelectElement>): void {
    const val = e.target.value;
    if (val === CUSTOM_SCALE_VALUE) return;
    const preset = SCALE_PRESETS.find((p) => p.label === val);
    if (!preset) return;
    dispatch({
      type: "geometry/viewport-set-scale",
      source: "scene",
      viewportId: viewport!.id,
      scaleNum: preset.num,
      scaleDenom: preset.denom,
    });
  }

  function handleLockClick(): void {
    dispatch({
      type: "geometry/viewport-set-lock",
      source: "scene",
      viewportId: viewport!.id,
      locked: !effectiveLocked,
    });
  }

  const lockLabel = effectiveLocked
    ? t("vp.statusbar.unlock_viewport")
    : t("vp.statusbar.lock_viewport");

  return (
    <div className="vp-status-controls" data-testid="vp-status-controls">
      <select
        className="vp-statusbar-scale"
        data-testid="vp-statusbar-scale-select"
        value={selectValue}
        title={t("vp.statusbar.scale.label")}
        aria-label={t("vp.statusbar.scale.label")}
        onChange={handleScaleChange}
      >
        {SCALE_PRESETS.map((p) => (
          <option key={p.label} value={p.label}>
            {p.label}
          </option>
        ))}
        {selectValue === CUSTOM_SCALE_VALUE ? (
          <option value={CUSTOM_SCALE_VALUE}>
            {t("vp.statusbar.scale.custom")}
          </option>
        ) : null}
      </select>

      <button
        type="button"
        className={`vp-statusbar-lock-btn${effectiveLocked ? " is-locked" : ""}`}
        data-testid="vp-statusbar-lock-btn"
        title={lockLabel}
        aria-label={lockLabel}
        aria-pressed={effectiveLocked}
        onClick={handleLockClick}
      >
        {effectiveLocked ? <LockClosedIcon /> : <LockOpenIcon />}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Public component — derives the active/selected VP from store; renders null
// if neither a VP is selected in PSPACE nor an active VP exists in
// MSPACE_VIA_VP.
// ---------------------------------------------------------------------------

export function ViewportStatusControls(): React.ReactElement | null {
  // Fine-grained subscriptions to avoid full re-render on unrelated changes.
  const geometryDocument = useEditorStore((s) => s.geometryDocument);
  const selectionEntityIds = useEditorStore((s) => s.state.selection.entityIds);
  const drawingSpaces = useEditorStore((s) => s.state.drawing.spaces);
  const activeSpaceId = useEditorStore((s) => s.state.drawing.activeSpaceId);
  const mode = useSpaceContextStore((s) => s.mode);
  const activeViewportId = useSpaceContextStore((s) => s.activeViewportId);

  // Case B: MSPACE_VIA_VP — show the active viewport's controls.
  if (mode === "MSPACE_VIA_VP") {
    if (!activeViewportId) return null;
    const viewport = findViewportById(geometryDocument, activeViewportId);
    if (!viewport) return null;
    if (viewport.viewportId === 1) return null;
    return <VpScaleLock viewportId={activeViewportId} />;
  }

  // Case A: PSPACE — show when exactly one Viewport entity is selected.
  const activeSpace = drawingSpaces.find((s) => s.id === activeSpaceId);
  if (activeSpace?.kind !== "layout") return null;

  if (selectionEntityIds.length !== 1) return null;

  const vpId = selectionEntityIds[0];
  const viewport = findViewportById(geometryDocument, vpId);

  if (!viewport) return null;
  // Paper-space control viewport (viewportId=1) is not user-manageable.
  if (viewport.viewportId === 1) return null;

  return <VpScaleLock viewportId={vpId} />;
}
